import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ClipboardCheck,
  HandCoins,
  HeartHandshake,
  LayoutDashboard,
  MessageSquare,
  ScrollText,
  ShieldCheck,
  Smartphone,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/utils/cn';

import {
  LANDING_FEATURE_SCREENSHOTS,
  type LandingFeatureScreenshot,
} from './landing-assets';
import { LandingReveal } from './landing-shared';

type FeatureKey =
  | 'dashboard'
  | 'people'
  | 'roles'
  | 'groups'
  | 'register'
  | 'content'
  | 'giving'
  | 'app'
  | 'attendance'
  | 'messaging'
  | 'care'
  | 'finances';

type FeatureAspect = {
  key: Exclude<FeatureKey, 'attendance' | 'messaging' | 'care' | 'finances'>;
  icon: LucideIcon;
  screenshots: ReadonlyArray<LandingFeatureScreenshot>;
  layout?: 'split' | 'stacked' | 'portrait';
};

const FEATURE_ASPECTS: ReadonlyArray<FeatureAspect> = [
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.dashboardCustomize],
  },
  {
    key: 'people',
    icon: Users,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.people],
  },
  {
    key: 'roles',
    icon: ShieldCheck,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.role],
  },
  {
    key: 'groups',
    icon: UsersRound,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.group, LANDING_FEATURE_SCREENSHOTS.committee],
  },
  {
    key: 'register',
    icon: ScrollText,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.marriage],
    layout: 'portrait',
  },
  {
    key: 'content',
    icon: BookOpen,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.content],
    layout: 'stacked',
  },
  {
    key: 'giving',
    icon: HandCoins,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.fund],
    layout: 'portrait',
  },
  {
    key: 'app',
    icon: Smartphone,
    screenshots: [LANDING_FEATURE_SCREENSHOTS.app],
    layout: 'portrait',
  },
];

const SUMMARY_FEATURES: ReadonlyArray<{
  key: 'people' | 'giving' | 'attendance' | 'messaging' | 'content' | 'care' | 'finances' | 'app';
  icon: LucideIcon;
}> = [
  { key: 'people', icon: Users },
  { key: 'giving', icon: HandCoins },
  { key: 'attendance', icon: ClipboardCheck },
  { key: 'messaging', icon: MessageSquare },
  { key: 'content', icon: BookOpen },
  { key: 'care', icon: HeartHandshake },
  { key: 'finances', icon: Wallet },
  { key: 'app', icon: Smartphone },
];

function FeatureScreenshot({
  screenshot,
  alt,
  className,
}: Readonly<{
  screenshot: LandingFeatureScreenshot;
  alt: string;
  className?: string;
}>) {
  return (
    <div className={cn('landing-feature-aspect-frame', className)}>
      <img
        src={screenshot.src}
        alt={alt}
        width={screenshot.width}
        height={screenshot.height}
        className="block h-auto w-full"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function FeatureAspectCopy({
  featureKey,
  icon: Icon,
  headingId,
}: Readonly<{ featureKey: FeatureAspect['key']; icon: LucideIcon; headingId?: string }>) {
  const { t } = useTranslation('landing');

  return (
    <div className="max-w-lg">
      <span className="landing-feature-icon landing-feature-icon-on-brand mb-4 inline-flex" aria-hidden>
        <Icon className="size-5 stroke-[1.75]" />
      </span>
      <h3
        id={headingId}
        className="landing-display text-balance text-[1.5rem] font-semibold tracking-tight text-on-brand sm:text-[1.75rem]"
      >
        {t(`features.${featureKey}.title`)}
      </h3>
      <p className="mt-3 text-pretty text-body leading-relaxed text-on-brand/90 sm:text-card-title">
        {t(`features.${featureKey}.body`)}
      </p>
    </div>
  );
}

function FeatureAspectVisuals({
  featureKey,
  screenshots,
  layout = 'split',
  t,
}: Readonly<{
  featureKey: FeatureAspect['key'];
  screenshots: ReadonlyArray<LandingFeatureScreenshot>;
  layout?: FeatureAspect['layout'];
  t: (key: string) => string;
}>) {
  if (screenshots.length > 1) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
        {screenshots.map((screenshot) => (
          <FeatureScreenshot
            key={screenshot.altKey}
            screenshot={screenshot}
            alt={t(screenshot.altKey)}
          />
        ))}
      </div>
    );
  }

  const screenshot = screenshots[0];
  if (!screenshot) return null;

  if (layout === 'stacked') {
    return <FeatureScreenshot screenshot={screenshot} alt={t(screenshot.altKey)} />;
  }

  return (
    <FeatureScreenshot
      screenshot={screenshot}
      alt={t(screenshot.altKey)}
      className={cn(
        layout === 'portrait' && 'mx-auto w-full max-w-[17.5rem] sm:max-w-xs lg:max-w-sm',
        featureKey === 'app' && 'rounded-[1.35rem]',
      )}
    />
  );
}

function FeatureAspectSection({
  aspect,
  index,
}: Readonly<{ aspect: FeatureAspect; index: number }>) {
  const { t } = useTranslation('landing');
  const reverse = index % 2 === 1;
  const stacked = aspect.layout === 'stacked';

  return (
    <LandingReveal
      as="section"
      delayMs={index * 40}
      className="py-14 first:pt-8 sm:py-16 sm:first:pt-10 lg:py-20 lg:first:pt-12"
      aria-labelledby={`feature-${aspect.key}-heading`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            stacked
              ? 'flex flex-col gap-10 lg:gap-12'
              : cn(
                  'grid items-center gap-10 lg:grid-cols-2 lg:gap-16 xl:gap-20',
                  reverse && '[&>*:first-child]:lg:order-2 [&>*:last-child]:lg:order-1',
                ),
          )}
        >
          <div className={cn(stacked && 'max-w-2xl')}>
            <FeatureAspectCopy
              featureKey={aspect.key}
              icon={aspect.icon}
              headingId={`feature-${aspect.key}-heading`}
            />
          </div>
          <div className={cn(stacked && 'w-full')}>
            <FeatureAspectVisuals
              featureKey={aspect.key}
              screenshots={aspect.screenshots}
              layout={aspect.layout}
              t={t}
            />
          </div>
        </div>
      </div>
    </LandingReveal>
  );
}

export const LandingFeaturesSummaryStrip = () => {
  const { t } = useTranslation('landing');

  return (
    <ul
      className="mt-12 grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-10"
      aria-label={t('featuresPage.summaryAria')}
    >
      {SUMMARY_FEATURES.map(({ key, icon: Icon }, index) => (
        <LandingReveal key={key} as="li" delayMs={index * 40}>
          <div className="landing-feature-item">
            <span className="landing-feature-icon landing-feature-icon-on-brand" aria-hidden>
              <Icon className="size-5 stroke-[1.75]" />
            </span>
            <div className="min-w-0">
              <h3 className="text-card-title font-semibold tracking-tight text-on-brand">
                {t(`featuresPage.summary.${key}.title`)}
              </h3>
              <p className="mt-0.5 text-body-sm leading-snug text-on-brand/85">
                {t(`featuresPage.summary.${key}.body`)}
              </p>
            </div>
          </div>
        </LandingReveal>
      ))}
    </ul>
  );
};

export const LandingFeaturesAspects = () => (
  <div className="text-on-brand">
    {FEATURE_ASPECTS.map((aspect, index) => (
      <FeatureAspectSection key={aspect.key} aspect={aspect} index={index} />
    ))}
  </div>
);
